import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { invitationAPI } from '@/services/api';
import { CalendarCheck, Loader2, Mail, CheckCircle2, AlertCircle, ArrowRight } from 'lucide-react';

export default function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    loadInvite();
  }, [token]);

  const loadInvite = async () => {
    try {
      const res = await invitationAPI.getDetails(token);
      if (!res.data.hasAccount) {
        navigate(`/invite-setup/${token}`, { replace: true });
        return;
      }
      setInvite(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Invitation not found or expired');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (e) => {
    e.preventDefault();
    setError('');
    setAccepting(true);

    try {
      await invitationAPI.accept(token, {});
      setAccepted(true);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.errors?.[0]?.msg || 'Could not accept invitation');
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--dash-bg)' }}>
        <div className="flex items-center gap-3" style={{ color: 'var(--dash-text3)' }}>
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>Loading invitation...</span>
        </div>
      </div>
    );
  }

  if (error && !invite) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: 'var(--dash-bg)' }}>
        <div
          className="w-full max-w-md rounded-2xl p-8 text-center"
          style={{ background: 'var(--dash-bg2)', border: '1px solid var(--dash-border)' }}
        >
          <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(248,113,113,.1)' }}>
            <AlertCircle className="w-6 h-6" style={{ color: '#f87171' }} />
          </div>
          <h2 className="text-lg font-semibold mb-2" style={{ color: 'var(--dash-text)' }}>{error}</h2>
          <p className="text-sm mb-6" style={{ color: 'var(--dash-text3)' }}>
            This invitation may have expired, already been used, or the link is invalid.
          </p>
          <a
            href="/"
            className="inline-flex items-center justify-center w-full h-10 rounded-lg text-sm font-semibold transition-opacity hover:opacity-90"
            style={{ background: 'var(--dash-grad-btn)', color: 'var(--dash-bg)' }}
          >
            Go to Home
          </a>
        </div>
      </div>
    );
  }

  if (accepted) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: 'var(--dash-bg)' }}>
        <div
          className="w-full max-w-md rounded-2xl p-8 text-center"
          style={{ background: 'var(--dash-bg2)', border: '1px solid var(--dash-border)' }}
        >
          <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(74,222,128,.1)' }}>
            <CheckCircle2 className="w-6 h-6" style={{ color: '#4ade80' }} />
          </div>
          <h2 className="text-lg font-semibold mb-2" style={{ color: 'var(--dash-text)' }}>Welcome to {invite.teamName}!</h2>
          <p className="text-sm mb-6" style={{ color: 'var(--dash-text3)' }}>
            Your invitation has been accepted. Log in to access the dashboard.
          </p>
          <button
            onClick={() => navigate('/login')}
            className="inline-flex items-center justify-center gap-2 w-full h-10 rounded-lg text-sm font-semibold transition-opacity hover:opacity-90"
            style={{ background: 'var(--dash-grad-btn)', color: 'var(--dash-bg)' }}
          >
            Go to Login <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12" style={{ background: 'var(--dash-bg)' }}>
      <div
        className="w-full max-w-md rounded-2xl p-8"
        style={{ background: 'var(--dash-bg2)', border: '1px solid var(--dash-border)' }}
      >
        <div className="text-center mb-6">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center mx-auto mb-3"
            style={{ background: 'var(--dash-grad-btn)' }}
          >
            <CalendarCheck className="w-5 h-5" style={{ color: 'var(--dash-bg)' }} />
          </div>
          <h1 className="text-xl font-bold" style={{ color: 'var(--dash-text)' }}>Team Invitation</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--dash-text3)' }}>
            <span style={{ color: 'var(--dash-text2)' }}>{invite.inviterName}</span> invited you to join{' '}
            <span className="font-semibold" style={{ color: 'var(--dash-text)' }}>{invite.teamName}</span>
          </p>
          <div className="mt-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-medium" style={{ background: 'var(--dash-surface2)', color: 'var(--dash-text2)' }}>
            Role: <span className="font-semibold capitalize">{invite.role}</span>
          </div>
        </div>

        {error && (
          <div className="mb-4 flex items-center gap-2 rounded-lg p-3 text-sm" style={{ background: 'rgba(248,113,113,.1)', color: '#f87171' }}>
            <AlertCircle className="w-4 h-4 shrink-0" />
            {error}
          </div>
        )}

        <form onSubmit={handleAccept} className="space-y-4">
          <div className="flex items-center gap-3 p-3 rounded-lg" style={{ background: 'var(--dash-surface)' }}>
            <Mail className="w-4 h-4 shrink-0" style={{ color: 'var(--dash-text3)' }} />
            <span className="text-sm font-medium" style={{ color: 'var(--dash-text2)' }}>{invite.email}</span>
          </div>

          <button
            type="submit"
            disabled={accepting}
            className="w-full h-10 rounded-lg text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
            style={{ background: 'var(--dash-grad-btn)', color: 'var(--dash-bg)' }}
          >
            {accepting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Joining...
              </>
            ) : (
              <>
                <CheckCircle2 className="w-4 h-4" />
                Join Team
              </>
            )}
          </button>
        </form>

        <p className="text-center text-xs mt-4" style={{ color: 'var(--dash-text4)' }}>
          Already have an account? Click "Join Team" to get access.
        </p>

        <p className="text-center text-[11px] mt-6" style={{ color: 'var(--dash-text5)' }}>
          Invitation expires on {new Date(invite.expiresAt).toLocaleDateString()}
        </p>
      </div>
    </div>
  );
}
